import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const movie = movies.find((m) => m.id === movieId);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要查找的影片不存在或已下架</p>
            <Link to="/" className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors">
              <i className="ri-home-4-line mr-2"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === movie.category);
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: category?.name || '影片', path: `/category/${movie.category}` },
    { label: movie.title },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-full lg:w-[320px]">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} - 屁屁影院高清在线观看`}
                    className="w-full h-full object-cover object-top"
                    title={`${movie.title} - 屁屁影院高清在线观看`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-accent-100 text-accent-700 text-sm font-semibold">
                    <i className="ri-star-fill text-xs"></i>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {category?.name || movie.category}
                  </Link>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                    剧情简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {movie.description}
                  </p>
                </div>

                {movie.highlights && movie.highlights.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                      影片亮点
                    </h2>
                    <ul className="space-y-2">
                      {movie.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-foreground-600">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-xs"></i>
                          </div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-background-200">
                  <button className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即播放
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-heart-line text-lg"></i>
                    收藏影片
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-14">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5"
                  data-product-shop
                >
                  {relatedMovies.map((m) => (
                    <MovieCard
                      key={m.id}
                      id={m.id}
                      title={m.title}
                      cover={m.cover}
                      rating={m.rating}
                      year={m.year}
                      tags={m.tags}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${movie.title} - 屁屁影院高清在线观看`,
            "description": movie.description,
            "image": movie.cover,
            "datePublished": `${movie.year}-01-01`,
            "dateModified": movie.updateDate,
            "author": {
              "@type": "Organization",
              "name": "屁屁影院"
            },
            "publisher": {
              "@type": "Organization",
              "name": "屁屁影院",
              "url": "https://streampops.github.io/"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://streampops.github.io/article/${movie.id}`
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://streampops.github.io/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": category?.name || '影片',
                "item": `https://streampops.github.io/category/${movie.category}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": movie.title,
                "item": `https://streampops.github.io/article/${movie.id}`
              }
            ]
          })
        }}
      />
    </>
  );
}